import { AskPdfApi } from '@setvi/shared/enums';

import { AskPdfStreamEvent } from './Types';

/**
 * Streams an answer over SSE.
 *
 * We hand-roll this instead of going through `axiosHelper`: XHR (and so
 * axios) buffers the whole body in the browser, and `EventSource` cannot
 * issue a POST or set an Authorization header. `fetch` + a ReadableStream
 * is the only option that gives us token-by-token rendering.
 */
interface StreamAnswerParams {
  conversationId: string;
  question: string;
  signal?: AbortSignal;
  onEvent: (event: AskPdfStreamEvent) => void;
}

const decodeFrame = (frame: string): AskPdfStreamEvent | null => {
  let eventName = '';
  const dataLines: string[] = [];

  frame.split('\n').forEach(line => {
    if (line.startsWith(':')) return; // heartbeat / comment
    if (line.startsWith('event:')) eventName = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  });

  if (!eventName || !dataLines.length) return null;

  try {
    const payload = JSON.parse(dataLines.join('\n'));
    return { type: eventName, ...payload } as AskPdfStreamEvent;
  } catch {
    // A malformed frame is not worth tearing the stream down for.
    return null;
  }
};

export const streamAnswer = async ({
  conversationId,
  question,
  signal,
  onEvent
}: StreamAnswerParams) => {
  const endpoint = AskPdfApi.SendMessage.replace(
    ':conversationId',
    conversationId
  );

  const response = await fetch(
    `${process.env.ASK_PDF_API_BASE_URL}${endpoint}`,
    {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${localStorage.token}`
      },
      body: JSON.stringify({ question })
    }
  );

  if (!response.ok || !response.body) {
    onEvent({
      type: 'error',
      message:
        response.status === 429
          ? 'Too many questions right now. Give it a moment and try again.'
          : 'The assistant is unavailable. Please try again.',
      code: String(response.status)
    });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Frames are separated by a blank line; the tail may be partial.
    const frames = buffer.split('\n\n');
    buffer = frames.pop() || '';

    frames.forEach(frame => {
      const event = decodeFrame(frame);
      if (event) onEvent(event);
    });
  }

  const trailing = decodeFrame(buffer);
  if (trailing) onEvent(trailing);
};
